<template>
  <section class="flex-grow">
    <EventDetailsAlert
      :alerts="alerts"
    />
    <header class="event-details">
      <div class="aspect-square h-auto sm:w-64 sm:h-64 w-full">
        <img
          :src="coverImageUrl"
          :alt="`${event.name} Logo`"
          class="object-cover h-full w-full"
        >
      </div>
      <div class="bg-content flex-grow p-2 sm:p-4">
        <h3
          v-if="event.promoter"
          class="text-xs sm:text-sm text-text-muted italic"
        >
          {{ event.promoter }}
        </h3>
        <h2
          class="font-bold text-on-content"
          :class="titleSizeClass"
        >
          {{ event.name }}
        </h2>
        <h3
          v-if="event.support"
          class="text-xs sm:text-sm text-on-content not-italic"
        >
          {{ event.support }}
        </h3>
        <div class="flex-col sm:flex-row gap-1 sm:gap-2 flex justify-between mt-1 sm:mt-3 md:items-center">
          <div class="text-xs sm:text-sm text-text-muted">
            <time>{{ eventDate }}</time>
            <template v-if="isMultiDay">
              -
              <time>{{ eventEndDate }}</time>
            </template>
            <template v-else>
              <br>
              <time>{{
                $gettext('%{time} Doors', { time: doorTime }) + ', ' + $gettext('%{time} Show', { time: startTime })
              }}</time>
            </template>
          </div>
          <address class="text-xs sm:text-sm text-text-muted not-italic">
            <h3 class="text-on-content">
              {{ event.venue?.name }}
            </h3>
            <h3>{{ venueAddress }}</h3>
          </address>
        </div>
        <MinimumAgeDisplay
          :minimum-age="minimumAge"
        />
      </div>
    </header>
    <div class="rounded-b-md overflow-hidden border-solid border-x border-b">
      <div
        v-if="announceDescription || event.artists?.length"
        class="bg-vp-blue-300 p-2 sm:p-4"
      >
        <div
          v-if="announceDescription"
          class="event-description-grid"
        >
          <div class="my-2 hidden md:block w-32 text-sm text-left font-semibold flex-shrink-0">
            {{ $gettext('About') }}
          </div>
          <div
            class="text-xs font-normal text-content-muted grow overflow-hidden md:inline-flex md:flex-row-reverse items-start"
          >
            <button
              v-if="canTruncateDescription || event.artists?.length"
              type="button"
              class="md:hidden px-4 -mx-4 float-right"
              @click="toggleAbout"
            >
              <iconify-icon
                icon="mdi:chevron-up"
                class="text-4xl transition-transform"
                :class="{ '-rotate-180': aboutTruncated }"
              />
            </button>
            <div
              v-if="description"
              class="vp-event-description overflow-hidden inline grow"
              v-html="description"
            />
            <h4
              v-else-if="event.artists?.length"
              class="lineup-label block md:hidden"
            >
              {{ $gettext("Lineup") }}
            </h4>
          </div>

          <button
            v-if="canTruncateDescription || event.artists?.length"
            type="button"
            class="hidden md:block px-4 float-right self-baseline"
            @click="toggleAbout"
          >
            <iconify-icon
              icon="mdi:chevron-up"
              class="text-4xl transition-transform"
              :class="{ '-rotate-180': aboutTruncated }"
            />
          </button>
          <template
            v-if="!aboutTruncated && event.artists?.length"
          >
            <h4 class="lineup-label hidden md:block">
              {{ $gettext("Lineup") }}
            </h4>
            <CheckoutArtists
              :artists="event.artists"
            />
            <div />
          </template>
        </div>
      </div>
      <div v-if="!props.hideTickets">
        <VPLoadingSpinner
          :loading="!ticketsLoaded"
          :top-message="$gettext('Loading tickets...')"
        >
          <div v-if="event?.usesSeatingChart">
            <SeatsioPicker />
          </div>
          <div v-if="shouldShowNoTicketsMessage">
            <div class="text-base text-content-muted p-8 font-bold flex items-center justify-center gap-2">
              <VPIcon
                icon="mdi:emoticon-confused-outline"
                class="text-xl flex-shrink-0"
              />
              <p>
                {{ $gettext('No tickets are currently configured for this event') }}
              </p>
            </div>
          </div>
          <div
            v-else
            ref="ticketRowsEl"
            class="-mb-1 border-vp-base-300 overflow-hidden tickets-grid"
          >
            <TicketRow
              v-for="ticket in ticketRows"
              :key="ticket.id"
              :ticket="ticket"
              :read-only="readOnly"
              :hide-quantity="event?.usesSeatingChart"
              :includes-fees="includesFees"
              :currency="currency"
            />

            <template v-if="addons.length">
              <div class="col-span-full p-4 mt-4 border-b border-b-vp-base-300">
                <h3 class="font-bold text-xl">
                  {{ $gettext('Add-Ons') }}
                </h3>
                <h4
                  v-show="!hasSelectedTickets"
                  class="text-content-muted"
                >
                  {{ $gettext('Select at least one ticket to be able to select add-ons!') }}
                </h4>
              </div>

              <TicketRow
                v-for="addon in addons"
                :key="addon.id"
                :inactive="!hasSelectedTickets"
                :ticket="addon"
                :read-only="addonsReadonly"
                :includes-fees="includesFees"
                :currency="currency"
              >
                <template #icon>
                  <iconify-icon
                    icon="mdi:star-box"
                    class="text-2xl"
                  />
                </template>
              </TicketRow>
            </template>
          </div>
        </VPLoadingSpinner>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
// Module scope, not `<script setup>`: the record has to outlive the component for "one
// `view_item` per event page view" to hold. "Empty cart" clears the store's selected event,
// which unmounts this behind Checkout.vue's `v-if="event"` and remounts it once the event is
// refetched — an instance-scoped flag would report the same page twice. Keying on the id is
// enough: a document serves one event, and a client-side move to a different one still
// reports. Never written on the server (the SSR guard returns first), so no request leaks
// into the next.
const reportedEventIds = new Set<number>();
</script>

<script setup lang="ts">
import { computed, onMounted, ref, toRef, watch } from 'vue';
import TicketRow from './TicketRow.vue';
import { DateTime } from 'luxon';
import { VPLoadingSpinner, VPIcon } from '@vp/vue-components';
import { useCheckoutStore } from './stores/checkout';
import { clientOnly } from 'vike-vue/clientOnly';
import type { Checkout } from '@vp/common';
import { Util } from '@vp/common';
import sanitizeHtml from 'sanitize-html';
import { useEventImage } from './composables/useEventImage';
import { useTruncatedHtml } from './composables/useTruncatedHtml';
import { sanitizeRichText } from './utils/sanitizeRichText';
import EventDetailsAlert from './EventDetailsAlert.vue';
import { ShoppingCartItemTypeEnum } from '@vp/common/generated/checkout.graphql';
import MinimumAgeDisplay from './MinimumAgeDisplay.vue';
import * as Sentry from '@sentry/vue';
import CheckoutArtists from './CheckoutArtists.vue';
import { HARD_CODED_USD_CHANGE_ME } from '@vp/common/util';
import { useTracking } from './components/tracking/useTracking';
import { trackingCurrency } from './components/tracking/trackingCurrency';
import { blueprintTrackingItems } from './components/tracking/trackingItems';
import { isSeatSelectable } from './utils/seatSelectable';
import { provideBestAvailableSeats } from './composables/useBestAvailableSeats';

const { LoadingState } = Util;

const SeatsioPicker = clientOnly(() => import('./SeatsioPicker.vue'));

type Props = {
  readOnly?: boolean;
  event: Checkout.CheckoutEvent.CheckoutEvent;
  hideTickets?: boolean;
};

const props = defineProps<Props>();
const event = toRef(props, 'event');
const announceDescription = computed(() => event.value.description ?? '');

const checkoutStore = useCheckoutStore();

// This page is the only common ancestor of the seating picker and the ticket rows —
// SeatsioPicker is behind clientOnly(), so a template ref never reaches it — so the channel
// the two talk over is provided from here.
const bestAvailable = provideBestAvailableSeats();
const ticketRowsEl = ref<HTMLElement | undefined>(undefined);

// A watcher rather than onMounted: the grid is the `v-else` of the no-tickets message,
// inside VPLoadingSpinner's slot, so it appears once the tickets resolve rather than at
// mount. No unmount teardown — the channel is constructed here and is garbage with this
// component (Vue stops the effect scope before unmounting the subtree, so a watcher would
// not fire then anyway).
watch(ticketRowsEl, el => bestAvailable.registerRows(el ?? undefined));

const titleSizeClass = computed(() => {
  const length = event.value.name?.length || 0;

  // TODO: if this ever gets any more entries, replace with a map+function.
  // Consult with @mak if needed.
  if (length > 150) {
    return 'text-xl sm:text-base lg:text-lg';
  }
  if (length > 120) {
    return 'text-2xl sm:text-lg lg:text-xl';
  }
  return 'text-2xl sm:text-3xl';
});

const eventDate = computed(() =>
  Util.formatTime(event.value.startTime, {
    timezone: event.value.venue?.timeZone,
    formatOptions: DateTime.DATE_MED_WITH_WEEKDAY,
  }),
);

const currency = computed(() => {
  if (event.value.venue?.currency) {
    return event.value?.venue?.currency;
  }
  return HARD_CODED_USD_CHANGE_ME;
});

const isMultiDay = computed(() => {
  try {
    if (!event.value.endTime) {
      return false;
    }

    const startTime = DateTime.fromISO(event.value.startTime);
    const endTime = DateTime.fromISO(event.value.endTime);

    const { days } = endTime.diff(startTime, 'days');

    if (days > 1) {
      return true;
    }

    return false;
  } catch (e) {
    Sentry.captureException(e);
    return false;
  }
});

const eventEndDate = computed(() => {
  try {
    if (!isMultiDay.value || !event.value.endTime) {
      return null;
    }

    return Util.formatTime(event.value.endTime, {
      timezone: event.value.venue?.timeZone,
      formatOptions: DateTime.DATE_MED_WITH_WEEKDAY,
    });
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
});

const doorTime = computed(() =>
  Util.formatTime(event.value.doorTime, { timezone: event.value.venue?.timeZone, formatOptions: DateTime.TIME_SIMPLE }),
);

const startTime = computed(() =>
  Util.formatTime(event.value.startTime, {
    timezone: event.value.venue?.timeZone,
    formatOptions: DateTime.TIME_SIMPLE,
  }),
);

const minimumAge = computed(() => event.value.minimumAge);

const ticketsLoaded = computed(() => checkoutStore.ticketsLoading == LoadingState.DONE);
const tickets = computed<Checkout.TicketBlueprint.CheckoutTicketBlueprint[]>(() =>
  checkoutStore.selectedEventTickets.filter(
    ({ ticketType }: Checkout.TicketBlueprint.CheckoutTicketBlueprint) => ticketType == 'ticket',
  ),
);
const addons = computed(() =>
  checkoutStore.selectedEventTickets.filter(
    ({ ticketType }: Checkout.TicketBlueprint.CheckoutTicketBlueprint) => ticketType == 'addon',
  ),
);

const alerts = computed(() => checkoutStore.getEventCheckoutConfig?.alerts);

const shouldShowNoTicketsMessage = computed(() =>
  alerts.value?.length === 0 && !props.hideTickets && !event.value?.usesSeatingChart && ticketsLoaded.value && tickets.value.length === 0 && addons.value.length === 0,
);

// The tier rows the page renders, and the same set it reports. A seated event's tiers are
// the ones the chart prices its categories from — wider than `tickets` above, and
// deliberately so (see `isSeatSelectable`); its rows carry no quantity control because the
// seats themselves are picked on the chart. Add-on rows render either way.
//
// The width has one visible consequence, accepted rather than guarded: SeatsioPicker groups
// its pricing by `category`, so a non-add-on blueprint configured without one is priced into
// no category and gets a row the chart offers no seat for. That blueprint is a seated-event
// misconfiguration either way, and naming it beats the old behaviour of showing the buyer no
// tier list at all — narrowing the set here would instead hide a tier the chart *can* sell.
const ticketRows = computed(() => {
  if (!event.value.usesSeatingChart) {
    return tickets.value;
  }

  return checkoutStore.selectedEventTickets.filter(isSeatSelectable);
});

// sc-19594: `view_item` used to fire from every TicketRow's onMounted, so a three-tier
// event reported three product views, each valued at one tier's price — and a seated
// event, which rendered no rows at the time, reported none at all. GA4 reads a list view
// as one event carrying every product.
const { trackEvent } = useTracking();

const viewItemRows = computed(() => blueprintTrackingItems([...ticketRows.value, ...addons.value]));

// The "from" price the buyer sees, so: tiers only — a cheap add-on must not undercut the
// headline price on any platform — and on-sale tiers only, since a sold-out tier's price
// is not one a new buyer can pay. Undefined when nothing purchasable is on sale (an
// add-on-only event); the payload then omits `value` rather than claiming zero.
const lowestTicketPrice = computed(() => {
  const prices = ticketRows.value
    .filter(({ soldOut }) => !soldOut)
    .map(({ breakdown }) => breakdown?.displayPrice)
    .filter((price): price is number => typeof price === 'number');

  return prices.length ? Math.min(...prices) : undefined;
});

// Nothing to report until the tickets resolve, while the presale gate hides them, or on
// an event with none configured — an empty `items` array is not a product view.
const viewItemReady = computed(
  () => ticketsLoaded.value && !props.hideTickets && viewItemRows.value.length > 0,
);

// A watcher rather than Payment.vue's `onMounted` + `until(...)` — the sibling page-level
// `begin_checkout` — because this one has to wait out the presale gate, which can lift long
// after mount: a watcher is stopped on unmount, where an `until()` promise would resolve
// into a dead component. The SSR guard is what `onMounted` would have given for free —
// an immediate watcher does run on the server.
watch(viewItemReady, ready => {
  // SSR first, so it reads as the build-time gate it is: Vite folds the literal and drops
  // the whole body from the server bundle, which is what keeps the module-scoped record
  // from ever being written in a process that serves every request.
  if (import.meta.env.SSR || !ready || reportedEventIds.has(event.value.id)) {
    return;
  }

  reportedEventIds.add(event.value.id);
  trackEvent('view_item', {
    currency: trackingCurrency(currency.value),
    ...(lowestTicketPrice.value === undefined ? {} : { value: lowestTicketPrice.value }),
    items: viewItemRows.value,
  });
}, { immediate: true });

const coverImageUrl = useEventImage(event);
const hasSelectedTickets = computed(() =>
  checkoutStore.getCartedTickets.some(
    ({ itemType }: Checkout.ShoppingCartItem.ShoppingCartItem) =>
      (itemType == ShoppingCartItemTypeEnum.Ticket || itemType == ShoppingCartItemTypeEnum.SeatTicket) && true, // tickets.value.some(({ id }) => id == itemId),
  ),
);

const addonsReadonly = computed(() => props.readOnly);

const includesFees = computed(() =>
  [event.value.config?.includeConvenienceFee, event.value.config?.includeTaxFee, event.value.config?.includeCcFee].some(
    v => v,
  ),
);

const venueAddress = computed(() => {
  const venue = props.event?.venue;
  if (venue) {
    return `${venue.street1}, ${venue.city}, ${venue.state}`;
  } else {
    return '';
  }
});

const sanitizer = (text: string, truncate: boolean) => {
  if (!truncate) {
    return sanitizeRichText(text);
  }

  return sanitizeHtml(text);
};

const {
  text: description,
  truncate: aboutTruncated,
  canTruncate: canTruncateDescription,
} = useTruncatedHtml(announceDescription, {
  limit: 150,
  startTruncated: false,
  sanitizer,
});

onMounted(() => {
  // In order for SSR to render the full description
  aboutTruncated.value = true;
});

const toggleAbout = () => {
  aboutTruncated.value = !aboutTruncated.value;
};

</script>
